import { Injectable, inject } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpClient,
  HttpXsrfTokenExtractor,
} from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

@Injectable()
export class CsrfInterceptor implements HttpInterceptor {
  private http = inject(HttpClient);
  private csrfExtract = inject(HttpXsrfTokenExtractor);

  private csrfCookieName = 'XSRF-TOKEN'; // Name of CSRF cookie
  private csrfApiUrl = `${environment.apiBaseUrl}csrf-cookie`; // Laravel CSRF endpoint (same as customer app)
  private isFetchingCsrf = false; // Flag to prevent parallel CSRF requests
  private csrfFetchObservable: Observable<any> | null = null;

  private cookieExists(): boolean {
    return document.cookie
      .split(';')
      .some((cookie) => cookie.trim().startsWith(`${this.csrfCookieName}=`));
  }

  private fetchCsrfToken(): Observable<any> {
    if (!this.isFetchingCsrf) {
      this.isFetchingCsrf = true;
      this.csrfFetchObservable = this.http
        .get(this.csrfApiUrl, { withCredentials: true })
        .pipe(
          catchError((error) => {
            console.error('CSRF token could not be fetched:', error);
            return of(null);
          }),
          switchMap(() => {
            this.isFetchingCsrf = false;
            this.csrfFetchObservable = null;
            return of(null);
          }),
        );
    }
    return this.csrfFetchObservable!;
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    // Exclude CSRF request itself
    if (req.url.includes(this.csrfApiUrl)) {
      return next.handle(req);
    }

    const handleRequestWithCsrf = (token: string) => {
      const csrfReq = req.clone({
        headers: req.headers.set('X-XSRF-TOKEN', token),
        withCredentials: true,
      });
      return next.handle(csrfReq);
    };

    // Check if CSRF cookie exists
    if (this.cookieExists()) {
      const csrf = this.csrfExtract.getToken()?.toString() || '';
      return handleRequestWithCsrf(csrf);
    }

    // If no CSRF cookie exists, fetch token and execute request
    return this.fetchCsrfToken().pipe(
      switchMap(() => {
        const csrf = this.csrfExtract.getToken()?.toString() || '';
        return handleRequestWithCsrf(csrf);
      }),
    );
  }
}

